interface ProductMatch {
  href: string;
  text: string;
  productId?: string;
}

interface LinkPreview {
  url: string;
  title?: string;
  description?: string;
  image?: string;
}

export const enhanceContent = (html: string): string => {
  if (!html) return '';

  let enhanced = html;

  // Transform video embeds into containers for VideoProxyPlayer
  enhanced = enhanced.replace(/<div class="video-wrapper">([\s\S]*?)<\/div>\s*<\/div>/g, (match, content) => {
    // Extract content ID from the video embed
    const contentIdMatch = content.match(/data-content-id="([^"]+)"/);
    const titleMatch = content.match(/data-title="([^"]+)"/);

    if (contentIdMatch) {
      const contentId = contentIdMatch[1];
      const title = titleMatch ? titleMatch[1] : '   ';

      // Create a container that will be replaced with VideoProxyPlayer
      return `<div class="video-proxy-container" data-content-id="${contentId}" data-title="${title}"></div>`;
    }

    // Also check if there's a direct video link
    const linkMatch = content.match(/<a[^>]+href="\/view\?id=([^"]+)"[^>]*>([^<]+)<\/a>/);
    if (linkMatch) {
      const contentId = linkMatch[1];
      const title = linkMatch[2] || '   ';

      return `<div class="video-proxy-container" data-content-id="${contentId}" data-title="${title}"></div>`;
    }

    return match; // Return original if we can't parse it
  });

  // Also handle standalone video links (not inside video-wrapper)
  enhanced = enhanced.replace(
    /<a\s+href="\/view\?id=([^"]+)"[^>]*>([^<]+)<\/a>/g,
    (match, contentId, linkText) => {
      // Create a container for the video proxy player
      return `
        ${match}
        <div class="video-proxy-container" data-content-id="${contentId}" data-title="${linkText}"></div>
      `;
    },
  );

  // Clean up complex HTML from external sources (like Medium)
  enhanced = enhanced.replace(/style="[^"]*"/g, ''); // Remove inline styles
  // Remove existing classes except those required for immersive features
  enhanced = enhanced.replace(
    /class="(?!video-proxy-container|immersive-button(?:-wrapper)?|product-preview-container)[^"]*"/g,
    '',
  );
  enhanced = enhanced.replace(/<span[^>]*>([^<]*)<\/span>/g, '$1'); // Unwrap empty spans
  enhanced = enhanced.replace(/<p>\s*<\/p>/g, ''); // Remove empty paragraphs
  enhanced = enhanced.replace(/&nbsp;/g, ' '); // Replace non-breaking spaces

  // Remove small images (like avatars) that have width/height less than 100px
  enhanced = enhanced.replace(/<img[^>]*(?:width|height)="(?:[1-9]?\d)"[^>]*>/g, '');
  enhanced = enhanced.replace(/<img[^>]*loading="lazy"[^>]*(?:width|height)="(?:[1-9]?\d)"[^>]*>/g, '');

  // Typography enhancements with theme support
  enhanced = enhanced
    .replace(
      /<h1/g,
      '<h1 class="text-4xl md:text-5xl font-bold mb-6 mt-12 leading-tight tracking-tight text-[var(--reading-theme-text)]"',
    )
    .replace(
      /<h2/g,
      '<h2 class="text-3xl md:text-4xl font-bold mb-4 mt-10 leading-tight text-[var(--reading-theme-text)]"',
    )
    .replace(
      /<h3/g,
      '<h3 class="text-2xl md:text-3xl font-semibold mb-3 mt-8 text-[var(--reading-theme-text)]"',
    )
    .replace(
      /<h4/g,
      '<h4 class="text-xl md:text-2xl font-semibold mb-2 mt-6 text-[var(--reading-theme-text)]"',
    )
    .replace(/<p/g, '<p class="text-lg leading-relaxed mb-6 text-[var(--reading-theme-text-muted)]"')
    .replace(/<ul/g, '<ul class="list-disc list-outside mb-6 space-y-2 ml-6"')
    .replace(/<ol/g, '<ol class="list-decimal list-outside mb-6 space-y-2 ml-6"')
    .replace(/<li/g, '<li class="text-lg leading-relaxed text-[var(--reading-theme-text-muted)]"');

  // Enhanced blockquotes with animated quote marks
  enhanced = enhanced.replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gs, (match, content) => {
    return `
        <div class="relative my-12 mx-auto max-w-3xl">
          <div class="absolute -top-4 -left-4 text-6xl text-[var(--reading-theme-primary)] opacity-30 font-serif animate-pulse">"</div>
          <blockquote class="relative bg-[var(--reading-theme-surface)] rounded-2xl p-8 border-l-4 border-[var(--reading-theme-primary)] shadow-lg">
            <p class="text-xl md:text-2xl leading-relaxed italic text-[var(--reading-theme-text)] mb-4">
              ${content.trim()}
            </p>
          </blockquote>
          <div class="absolute -bottom-4 -right-4 text-6xl text-[var(--reading-theme-primary)] opacity-30 font-serif animate-pulse transform rotate-180">"</div>
        </div>
      `;
  });

  // Enhanced links with preview cards
  enhanced = enhanced.replace(/<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>/g, (match, href, text) => {
    // Check if it's an internal link
    const isInternal = href.startsWith('/') || href.includes(window?.location?.hostname || '');

    return `
        <a 
          href="${href}" 
          class="inline-flex items-center gap-1 text-[var(--reading-theme-primary)] hover:text-[var(--reading-theme-secondary)] underline decoration-2 underline-offset-2 transition-all hover:underline-offset-4 group"
          ${!isInternal ? 'target="_blank" rel="noopener noreferrer"' : ''}
        >
          ${text}
          ${
            !isInternal
              ? '<svg class="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>'
              : ''
          }
        </a>
      `;
  });

  // Transform embedded buttons into immersive cards (Substack-style)
  enhanced = enhanced.replace(/<div[^>]*data-type="button"[^>]*>([\s\S]*?)<\/div>/g, (match) => {
    // Extract button attributes
    const hrefMatch = match.match(/href="([^"]+)"/);
    const textMatch = match.match(/text="([^"]+)"/);
    const sizeMatch = match.match(/size="([^"]+)"/);
    const alignmentMatch = match.match(/alignment="([^"]+)"/);
    const bgColorMatch = match.match(/backgroundcolor="([^"]+)"/);
    const textColorMatch = match.match(/textcolor="([^"]+)"/);

    // Also extract from the inner anchor tag
    const anchorMatch = match.match(/<a[^>]*href="([^"]+)"[^>]*>([^<]+)<\/a>/);

    const href = hrefMatch?.[1] || anchorMatch?.[1] || '#';
    const text = textMatch?.[1] || anchorMatch?.[2] || 'Click Here';
    const size = sizeMatch?.[1] || 'medium';
    const alignment = alignmentMatch?.[1] || 'center';
    const bgColor = bgColorMatch?.[1] || 'var(--reading-theme-primary)';
    const textColor = textColorMatch?.[1] || '#ffffff';

    // Determine button type and icon
    let buttonType = 'link';
    let icon = '';
    let subtitle = '';

    if (href.includes('productId=')) {
      buttonType = 'product';
      icon = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
        </svg>`;
      subtitle = 'View Product';
    } else if (href.includes('/lk/') || href.includes('/view?id=')) {
      buttonType = 'story';
      icon = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
        </svg>`;
      subtitle = 'Read Story';
    } else {
      icon = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
        </svg>`;
      subtitle = 'External Link';
    }

    // Size classes
    const sizeClasses = {
      small: 'px-4 py-2 text-sm',
      medium: 'px-6 py-3 text-base',
      big: 'px-8 py-4 text-lg',
    };

    // Create immersive button card
    return `
        <div class="immersive-button-wrapper my-8" style="text-align: ${alignment};">
          <a 
            href="${href}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="immersive-button inline-flex items-center justify-center ${
              sizeClasses[size] || sizeClasses.medium
            } rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl relative overflow-hidden group no-underline"
            style="background-color: ${bgColor}; color: ${textColor};"
            data-button-type="${buttonType}"
          >
            <!-- Animated background gradient -->
            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            <!-- Button content -->
            <span class="relative flex items-center gap-3">
              ${icon}
              <span class="flex flex-col items-start">
                <span class="font-bold">${text}</span>
                ${subtitle ? `<span class="text-xs opacity-80">${subtitle}</span>` : ''}
              </span>
              <svg class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </span>
          </a>
        </div>
      `;
  });

  // Transform product links in buttons into rich product cards
  enhanced = enhanced.replace(
    /<a[^>]*href="([^"]*productId=[^"]*)"[^>]*class="immersive-button[^"]*"[^>]*>([\s\S]*?)<\/a>/g,
    (match, href) => {
      const productIdMatch = href.match(/productId=([^&]+)/);
      const productId = productIdMatch?.[1];

      if (!productId) return match;

      // Return the original button but add a product preview container after it
      return `
        ${match}
        <div class="product-preview-container mt-4" data-product-id="${productId}" data-product-url="${href}">
          <!-- Product preview will be loaded here -->
        </div>
      `;
    },
  );

  // Enhanced code blocks
  enhanced = enhanced.replace(/<pre[^>]*><code[^>]*>(.*?)<\/code><\/pre>/gs, (match, code) => {
    return `
        <div class="relative my-8 group">
          <div class="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <button class="px-3 py-1 text-xs bg-[rgba(var(--reading-theme-primary-rgb),0.2)] hover:bg-[rgba(var(--reading-theme-primary-rgb),0.3)] text-[var(--reading-theme-primary)] rounded-md transition-colors" onclick="navigator.clipboard.writeText(this.closest('pre').querySelector('code').textContent)">
              Copy
            </button>
          </div>
          <pre class="bg-[var(--reading-theme-surface)] rounded-2xl p-6 overflow-x-auto shadow-xl border border-[rgba(var(--reading-theme-primary-rgb),0.1)]"><code class="text-[var(--reading-theme-text-muted)] text-sm font-mono">${code}</code></pre>
        </div>
      `;
  });

  // Enhanced horizontal rules
  enhanced = enhanced.replace(
    /<hr[^>]*>/g,
    `<div class="relative my-16">
      <div class="absolute inset-0 flex items-center">
        <div class="w-full border-t border-gray-300 dark:border-gray-700"></div>
      </div>
      <div class="relative flex justify-center">
        <span class="bg-white dark:bg-gray-900 px-4">
          <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </span>
      </div>
    </div>`,
  );

  // Add drop cap to first paragraph if it's substantial
  const firstParagraphMatch = enhanced.match(/<p[^>]*>(.{50,})/);
  if (firstParagraphMatch) {
    enhanced = enhanced.replace(
      firstParagraphMatch[0],
      firstParagraphMatch[0].replace(
        /<p([^>]*)>/,
        '<p$1 class="first-letter:float-left first-letter:text-7xl first-letter:font-bold first-letter:mr-3 first-letter:mt-1 first-letter:text-[var(--reading-theme-primary)]">',
      ),
    );
  }

  return enhanced;
};

// Helper function to extract text from HTML
export const extractTextFromHtml = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

// Helper function to calculate reading time
export const calculateReadingTime = (text: string): number => {
  const wordsPerMinute = 200;
  const wordCount = text.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

// Helper function to generate table of contents from headings
export const generateTableOfContents = (html: string): Array<{ id: string; text: string; level: number }> => {
  const headings: Array<{ id: string; text: string; level: number }> = [];
  const regex = /<h([1-3])[^>]*>(.*?)<\/h[1-3]>/gi;
  let match;
  let index = 0;

  while ((match = regex.exec(html)) !== null) {
    const level = parseInt(match[1]);
    const text = match[2].replace(/<[^>]*>/g, ''); // Strip HTML tags
    const id = `heading-${index++}`;
    headings.push({ id, text, level });
  }

  return headings;
};

// Helper function to parse videos from content
export const parseVideosFromContent = (
  html: string,
): Array<{ contentId: string; title: string; position: string }> => {
  const videos: Array<{ contentId: string; title: string; position: string }> = [];

  // First, process the content to create video-proxy-containers
  const processedHtml = enhanceContent(html);

  // Match video-proxy-containers created by enhanceContent
  const videoProxyRegex =
    /<div class="video-proxy-container"[^>]*data-content-id="([^"]+)"[^>]*data-title="([^"]+)"[^>]*><\/div>/g;
  let match;
  let index = 0;

  while ((match = videoProxyRegex.exec(processedHtml)) !== null) {
    videos.push({
      contentId: match[1],
      title: match[2] || '   ',
      position: `video-${index++}`,
    });
  }

  return videos;
};
